package dev.lingvo.site.components.studio.editor

import androidx.compose.runtime.Composable
import dev.lingvo.site.ds.LSegmented
import dev.lingvo.site.ds.LSegmentedOption
import dev.lingvo.site.ds.LSelect
import dev.lingvo.site.ds.LSelectOption
import dev.lingvo.site.ds.Space
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.i18n.tf
import org.jetbrains.compose.web.dom.Div

/** Groups wrap as units; controls keep their intrinsic width and their natural keyboard order. */
@Composable
internal fun StudioToolbar(state: StudioUiState, dispatch: (StudioIntent) -> Unit) {
    fun action(type: StudioActionType) = dispatch(StudioIntent.Action(StudioAction(type)))
    Div(attrs = {
        attr("data-studio-region", "toolbar"); tabIndex(-1)
        attr("role", "group"); attr("aria-label", tr("studio.editor.studio_toolbar.canvas_tools", "Canvas tools"))
        style {
            property("display", "flex"); property("flex-wrap", "wrap"); property("align-items", "center")
            property("gap", Space.MD.toString()); property("padding", Space.SM.toString()); property("min-width", "0")
        }
    }) {
        StudioNameStatus(state.slots.find { it.id == state.view.slotId }?.title.orEmpty(), state.saveLabel)
        StudioToolbarGroup(t("studio.editor.studio_toolbar.history", "History")) {
            StudioButton(t("studio.editor.studio_toolbar.undo", "Undo"), state.canUndo && state.editable) { action(StudioActionType.UNDO) }
            StudioButton(t("studio.editor.studio_toolbar.redo", "Redo"), state.canRedo && state.editable) { action(StudioActionType.REDO) }
        }
        StudioToolbarGroup(t("studio.editor.studio_toolbar.view_and_zoom", "View and zoom")) {
            StudioButton(t("studio.editor.studio_toolbar.fit", "Fit")) { action(StudioActionType.FIT) }
            StudioButton(t("studio.editor.studio_toolbar.zoom_in", "Zoom in")) { action(StudioActionType.ZOOM_IN) }
            StudioButton(t("studio.editor.studio_toolbar.zoom_out", "Zoom out")) { action(StudioActionType.ZOOM_OUT) }
            StudioMetaText("${state.zoomPercent}%") // i18n-ignore
        }
        StudioToolbarGroup(t("studio.editor.studio_toolbar.tool", "Tool")) {
            LSegmented(listOf(
                LSegmentedOption(StudioActionType.SELECT_TOOL, t("studio.editor.studio_toolbar.select", "Select")),
                LSegmentedOption(StudioActionType.TEXT_TOOL, t("studio.editor.studio_toolbar.text", "Text"), state.editable),
                LSegmentedOption(StudioActionType.RECTANGLE_TOOL, t("studio.editor.studio_toolbar.rectangle", "Rectangle"), state.editable),
                LSegmentedOption(StudioActionType.HAND_TOOL, t("studio.editor.studio_toolbar.hand", "Hand")),
            ), state.tool, { action(it) }, label = t("studio.editor.studio_toolbar.tool", "Tool"))
        }
        Div(attrs = { style { property("flex", "0 1 248px"); property("min-width", "0") } }) {
            LSelect(state.view.device, state.document.boards.map { board -> LSelectOption(board.deviceClass,
                dev.lingvo.shared.DeviceClass.fromId(board.deviceClass)?.displayName ?: board.deviceClass) },
                { dispatch(StudioIntent.Device(it)) }, label = t("studio.editor.studio_toolbar.device", "Device"))
        }
        StudioToolbarGroup(t("studio.editor.studio_toolbar.insert", "Insert")) {
            StudioButton(t("studio.editor.studio_toolbar.add_text", "Add text"), state.editable) { dispatch(StudioIntent.Add("text")) }
            StudioButton(t("studio.editor.studio_toolbar.add_device_frame", "Add device frame"), state.editable) { dispatch(StudioIntent.Add("device")) }
        }
        state.languages.filter { it.fittedSize != null }.minByOrNull { it.fittedSize!! }?.let {
            StudioMetaText(tf("studio.smallest_fit", "Smallest: {size} px in {language}", "size" to it.fittedSize, "language" to it.label))
        }
        StudioToolbarGroup(t("studio.editor.studio_toolbar.help", "Help")) {
            StudioButton(t("studio.editor.studio_toolbar.keyboard_shortcuts", "Keyboard shortcuts")) { action(StudioActionType.SHORTCUTS) }
        }
    }
}

@Composable
private fun StudioToolbarGroup(label: String, content: @Composable () -> Unit) {
    Div(attrs = {
        attr("role", "group"); attr("aria-label", label); attr("data-studio-tool-group", "")
        style {
            property("display", "flex"); property("align-items", "center"); property("gap", Space.SM.toString())
            property("flex", "0 0 auto"); property("max-width", "100%")
        }
    }) { content() }
}
